import { Alert, Button, Card, CardColors, Image, Separator, Text } from '@/ui'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { TransactionRuleCategoryType } from '@/reducers/admin/transactionRulesReducer'

import { useAppDispatch } from '@/utils/typeHelpers'
import { fetchUserTransactions } from '@/features/transactions/transactions.slice'
import { Transaction } from '@/reducers/admin/allTransactions.slice'
import { ClarifyingModal } from './ClarifyingTransactionsModal'
import { useReselector } from '@/utils/sharedHooks'
import { DateTime } from 'luxon'
import { sampleTransactionData } from './SampleTransactionData'
import { SampleTransactionCard } from './SampleTransactionCard'
import { filterUserTransactions } from '@/features/transactions/transactions.selectors'
import { fetchTransactionCategoriesIfNeeded } from '@/features/reports/reports.slice'
import { isNil } from 'lodash'
import { updateUserOnboardingStep } from '../../user-onboarding-steps/onboarding.actions'
import { getUserStepIdByOnboardingStepId } from '../../user-onboarding-steps/onboarding.selectors'
import { BookkeepingOnboardingStep } from '../../user-onboarding-steps/onboarding.reducer'
import { AhaMomentsBookeepingHeader } from '../AhaMomentsHeaderFooter'
import { SampleTransactionCardHeader } from './SampleTransactionCardHeader'
import { TypographyStyles } from '@/styles'

export const ClarifyTransactionsExample = ({
  handleContinue,
  handleBack,
}: {
  handleContinue: () => void
  handleBack: () => void
}) => {
  const dispatch = useAppDispatch()
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)
  const [isSkipModalOpen, setIsSkipModalOpen] = useState(false)
  const [continueButtonDisabled, setContinueButtonDisabled] = useState(true)
  const [transactionsToDisplay, setTransactionsToDisplay] = useState<
    Transaction[]
  >([])
  const [displaySampleTransaction, setDisplaySampleTransaction] =
    useState(false)
  const [
    isClarifyingConfirmationModalOpen,
    setIsClarifyingConfirmationModalOpen,
  ] = useState(false)
  const { transactions } = useReselector(filterUserTransactions, 'all')
  const currentStepId = useReselector(
    getUserStepIdByOnboardingStepId,
    BookkeepingOnboardingStep.clarifyTransactionsExample
  )

  useEffect(() => {
    async function fetch() {
      await Promise.all([
        dispatch(fetchUserTransactions()),
        dispatch(fetchTransactionCategoriesIfNeeded()),
      ])
    }
    fetch()
  }, [dispatch])

  const handleContinueWithSampleData = () => {
    setIsClarifyingConfirmationModalOpen(true)
  }

  const handleContinueInClarifyingModal = () => {
    setIsClarifyingConfirmationModalOpen(false)
    handleContinue()
  }

  const amazonOrVenmoTransactionsToDisplay = useMemo(() => {
    return transactions
      .filter(
        (transaction) =>
          transaction.description.toLowerCase().includes('amazon') ||
          transaction.description.toLowerCase().includes('venmo')
      )
      .slice(0, 5)
  }, [transactions])

  useEffect(() => {
    const shouldDisplaySample = amazonOrVenmoTransactionsToDisplay.length === 0
    setDisplaySampleTransaction(shouldDisplaySample)

    setTransactionsToDisplay(
      shouldDisplaySample
        ? sampleTransactionData
        : amazonOrVenmoTransactionsToDisplay
    )
  }, [amazonOrVenmoTransactionsToDisplay])

  const updateSampleTransaction = useCallback(
    (transactionId: number, updates: Partial<Transaction>) => {
      setTransactionsToDisplay((currentTransactions) =>
        currentTransactions.map((transaction) =>
          transaction.id === transactionId
            ? { ...transaction, ...updates }
            : transaction
        )
      )
    },
    []
  )
  useEffect(() => {
    const allTransactionsValid = transactionsToDisplay.every((transaction) => {
      const hasType = !isNil(transaction.type)

      // If it's a personal transaction, only type is required
      if (transaction.type === TransactionRuleCategoryType.personal) {
        return hasType
      }

      // else - need type AND (category OR note)
      const hasCategory =
        !isNil(transaction.transactionCategory?.name) &&
        transaction.transactionCategory?.name.trim() !== ''

      const hasNote =
        !isNil(transaction.notes) && transaction.notes.trim() !== ''

      return hasType && (hasCategory || hasNote)
    })

    setContinueButtonDisabled(!allTransactionsValid)
  }, [transactionsToDisplay])

  const handleSkipContinue = async () => {
    if (currentStepId) {
      try {
        await updateUserOnboardingStep({
          id: currentStepId,
          updatableFields: { skippedAt: DateTime.now().toISO() },
        })(dispatch)
      } catch (error) {
        console.error('Error updating onboarding step:', error)
      }
    }
    handleContinue()
  }

  const handleOpenModal = (setter: (value: boolean) => void) => () =>
    setter(true)
  const handleCloseModal = (setter: (value: boolean) => void) => () =>
    setter(false)

  return (
    <>
      <AhaMomentsBookeepingHeader />
      <div className="flex flex-col max-w-[1000px] mx-auto p-0">
        <div className="py-4">
          <div className="mb-11">
            <Text variant={TypographyStyles.H2}>
              Learn How Bookkeeping Works
            </Text>
          </div>

          <Card color={CardColors.LEVEL_1}>
            <div className="flex flex-col md:flex-row">
              <div className="w-full md:w-1/4 mr-2.5 flex items-center">
                <Image
                  src="https://heard-images.s3.amazonaws.com/assets/clarify-transactions.png"
                  alt="Clarify transactions"
                />
              </div>
              <div className="w-full md:w-11/12">
                <Text variant={TypographyStyles.H3}>
                  Let&apos;s clarify some transactions!
                </Text>
                <div className="mt-2">
                  <Text>
                    We&apos;ve identified some unclear transactions. Here&apos;s
                    how to provide your input:
                  </Text>
                </div>
                <div className="mt-4 space-y-4">
                  <div className="flex">
                    <div className="w-6 flex items-start justify-center">
                      <Image
                        src="https://heard-images.s3.amazonaws.com/assets/number-1.png"
                        alt="Step 1"
                        height={25}
                        width={25}
                      />
                    </div>
                    <div className="ml-2">
                      <Text>
                        <strong>Select a type</strong>
                        <br />
                        Mark the transaction as either <strong>
                          Business
                        </strong>{' '}
                        or <strong>Personal</strong>.
                      </Text>
                    </div>
                  </div>

                  <div className="flex">
                    <div className="w-6 flex items-start justify-center">
                      <Image
                        src="https://heard-images.s3.amazonaws.com/assets/number-2.png"
                        alt="Step 2"
                        height={25}
                        width={25}
                      />
                    </div>
                    <div className="ml-2">
                      <Text>
                        <strong>Select a category or add a note</strong>
                        <br />
                        If it&apos;s a business transaction, select a category.
                        If you&apos;re not sure which category to use, you can
                        add a note by clicking{' '}
                        <b className="text-green-600">Add Note.</b>
                      </Text>
                    </div>
                  </div>
                </div>
                <div className="mt-5">
                  <Button
                    onClick={handleOpenModal(setIsVideoModalOpen)}
                    variant="link"
                  >
                    Watch tutorial video
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {displaySampleTransaction && (
          <Alert variant="warning">
            <Text variant={TypographyStyles.BODY_SM}>
              Note: This is <b>sample data</b> for demonstration purposes only.
            </Text>
          </Alert>
        )}

        <SampleTransactionCardHeader />
        {transactionsToDisplay.map((transaction) => (
          <SampleTransactionCard
            key={transaction.id}
            transaction={transaction}
            displaySampleTransaction={displaySampleTransaction}
            updateSampleTransaction={updateSampleTransaction}
          />
        ))}

        <Text className="mt-6">
          Don&apos;t have time for this now? Don&apos;t worry, your progress is
          automatically saved.
        </Text>
        <Separator />

        <div className="flex flex-col md:flex-row md:items-center">
          <Button
            className="w-full md:w-1/6"
            variant="secondary"
            size="md"
            onClick={handleBack}
          >
            Back
          </Button>
          <div className="w-full md:w-5/6 flex justify-end space-x-2 mt-4 md:mt-0">
            <Button
              variant="secondary"
              size="md"
              onClick={handleOpenModal(setIsSkipModalOpen)}
            >
              Skip for now
            </Button>
            <Button
              variant="primary"
              size="md"
              disabled={continueButtonDisabled}
              onClick={
                displaySampleTransaction
                  ? handleContinueWithSampleData
                  : handleContinue
              }
            >
              Continue
            </Button>
          </div>
        </div>
      </div>

      {isVideoModalOpen && (
        <ClarifyingModal
          open={isVideoModalOpen}
          onClose={handleCloseModal(setIsVideoModalOpen)}
          title="How to clarify transactions"
          content={
            <iframe
              src="https://www.loom.com/embed/a8860aa90adb41708ba88c4b874e34c3"
              width="100%"
              height="500px"
              allowFullScreen
              title="How to clarify transactions"
              sandbox="allow-scripts allow-same-origin allow-popups"
            />
          }
          actions={null}
        />
      )}

      {isSkipModalOpen && (
        <ClarifyingModal
          open={isSkipModalOpen}
          onClose={handleCloseModal(setIsSkipModalOpen)}
          title="Are you sure you want to skip?"
          content={
            <>
              {displaySampleTransaction ? (
                <Text>
                  You will likely need to clarify some of your actual
                  transactions later, so doing this step will help you learn how
                  to do so quickly and easily.
                </Text>
              ) : (
                <Text>
                  You will likely need to clarify these transactions later, so
                  this will help complete your books faster!
                </Text>
              )}
              <br />
              <Text>
                You can also take a break and come back to this step another
                time.
              </Text>
            </>
          }
          actions={
            <>
              <Button
                variant="link"
                className="mr-5"
                onClick={handleSkipContinue}
              >
                Skip
              </Button>
              <Button
                variant="primary"
                onClick={handleCloseModal(setIsSkipModalOpen)}
              >
                Return to clarifying
              </Button>
            </>
          }
        />
      )}

      {isClarifyingConfirmationModalOpen && (
        <ClarifyingModal
          open={isClarifyingConfirmationModalOpen}
          onClose={handleCloseModal(setIsClarifyingConfirmationModalOpen)}
          title="Thanks for clarifying!"
          content={
            <>
              <Image
                src="https://heard-images.s3.amazonaws.com/assets/clarifying.png"
                alt="Clarify transactions"
              />
              <Text>
                Transactions from vendors like Amazon and Venmo usually require
                clarification, since it&apos;s hard to tell if they&apos;re
                personal or for your business.
              </Text>
              <br />
              <Text>
                We&apos;ll let you know if we need you to do this again in the
                future!
              </Text>
            </>
          }
          actions={
            <Button variant="primary" onClick={handleContinueInClarifyingModal}>
              Continue Onboarding
            </Button>
          }
        />
      )}
    </>
  )
}
